import * as Sentry from '@sentry/nextjs';
import ts from 'typescript';

import { DEBUG_EVALUATE_EXPRESSIONS } from '@/configuration/client';

import { type BaseEvaluateExpressionError } from './errors';
import evaluators from './evaluators';
import { type EvaluateResponse } from './types';

const log = DEBUG_EVALUATE_EXPRESSIONS
  ? console
  : {
      error: () => undefined,
      info: () => undefined,
      log: () => undefined,
    };

const CACHE = new Map<string, ts.Expression | undefined>();

const EMPTY_PROFILE = {
  evaluateTime: 0,
  parseTime: 0,
  runs: 0,
};

let countsById: Record<string, number> = {};
let isProfileEnabled = false;
let thisProfile = { ...EMPTY_PROFILE };
export const enableProfiling = (): void => {
  isProfileEnabled = true;
  thisProfile = { ...EMPTY_PROFILE };
  countsById = {};
};
export const disableProfiling = (): void => {
  isProfileEnabled = false;
};

type ProfileData = {
  summary: typeof EMPTY_PROFILE;
  countsById: Record<string, number>;
  cacheSize: number;
  cacheMemory: number;
};
export const getProfilingData = (): ProfileData => ({
  cacheMemory: Buffer.byteLength(JSON.stringify(Array.from(CACHE.values()))),
  cacheSize: CACHE.size,
  countsById,
  summary: thisProfile,
});

// Use https://github.com/wessberg/ts-evaluator for hints about adding evaluators
const evaluateExpression = ({
  expression: initialExpression,
  variables,
  id,
}: {
  expression: string;
  id?: string;
  variables: Record<string, unknown>;
}): Exclude<EvaluateResponse, BaseEvaluateExpressionError> | Array<EvaluateResponse> => {
  let previousTime: number = 0;
  if (isProfileEnabled) {
    thisProfile.runs += 1;
    previousTime = Date.now();
    if (id) {
      if (countsById[id] === undefined) {
        countsById[id] = 0;
      }
      countsById[id] += 1;
    }
  }
  const expression = `var ___ = ${initialExpression}`;
  let node = CACHE.get(expression);
  if (!node) {
    const program = ts.createSourceFile('evaluate-expression.ts', expression, ts.ScriptTarget.ES2022);
    node = (program.statements[0] as ts.VariableStatement).declarationList.declarations[0].initializer;
    if (isProfileEnabled) {
      const thisTime = Date.now();
      thisProfile.parseTime += thisTime - previousTime;
      previousTime = thisTime;
    }
    CACHE.set(expression, node);
  }

  if (node) {
    const response = evaluators.expression({
      evaluators,
      log,
      node,
      variables,
    });
    if (isProfileEnabled) {
      const thisTime = Date.now();
      thisProfile.evaluateTime += thisTime - previousTime;
    }
    if (response instanceof Error) {
      console.error(response, initialExpression, 'evaluateExpression error');
      Sentry.captureException(response, {
        extra: { expression: initialExpression, variables },
      });
      return undefined;
    }

    return response;
  }

  const error = new Error('No node available in evaluateExpression');
  console.error(error, initialExpression, 'evaluateExpression error');
  Sentry.captureException(error, {
    extra: { initialExpression },
  });
  return undefined;
};

export default evaluateExpression;
